<script setup>
import bottleSvg from '@/assets/images/bottle.svg'
import bundleSvg from '@/assets/images/bundle.svg'
import { API_URL } from '@/constants/GlobalConstants.js'
import { buildImageUrlAppendix } from '@/services/helpers/ImageSizeHelper'
import UtilityHelper from '@/services/helpers/UtilityHelper'
import { useDebounceFn } from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { VImg } from 'vuetify/lib/components/index.js'
import { useAccessibilityStore } from '../../../stores/accessibility.js'

const {
  productId,
  campaignId,
  useHeroImage,
  alt,
  isBundle,
  height,
  width,
  observeFetchPriority
} = defineProps({
  height: {
    type: [Number, String],
    default: 300
  },
  width: {
    type: [Number, String],
    default: 300
  },
  productId: [Number, String],
  campaignId: [Number, String],
  useHeroImage: {
    default: false,
    type: Boolean
  },
  alt: String,
  isBundle: {
    default: false,
    type: Boolean
  },
  addItemProp: {
    default: false,
    type: Boolean
  },
  observeFetchPriority: {
    default: false,
    type: Boolean
  },
  /**
   * Render the image immediately instead of waiting for the
   * IntersectionObserver. For above-the-fold images (the PDP stage): a
   * server render has no observer, so only an eager image gets its src into
   * the SSR HTML — otherwise visitors watch the placeholder until hydration
   * (VIP-12038).
   */
  eager: {
    default: false,
    type: Boolean
  }
})

const accessibilityStore = useAccessibilityStore()

const highContrastEnabled = computed(
  () => accessibilityStore.highContrastEnabled
)
const type = computed(() => (useHeroImage ? 'image' : 'small_image'))

const imgHeight = ref(height)
const imgWidth = ref(width)

const imgRef = ref(null)
const vImgRef = ref(null)
let hasMeasured = false

// Deterministic 1x on the server AND during the hydration render (a DPR-based
// first render would mismatch the server markup); the mounted hook below
// swaps in the device density (VIP-12037)
const density = ref(1)

const image = computed(() => {
  if (!productId || !campaignId) return null

  const appendix = buildImageUrlAppendix(
    type.value,
    imgHeight.value * density.value,
    imgWidth.value * density.value
  )

  return UtilityHelper.formatString(
    API_URL.CATALOG.PRODUCT.GET_V2_IMAGE,
    productId,
    campaignId,
    appendix
  )
})

const placeholderImages = {
  bundle: bundleSvg,
  bottle: bottleSvg
}

/**
 * Intrinsic ratios of the placeholder SVGs above (bundle.svg is 200x156,
 * bottle.svg has a 150x250 viewBox) — keep in sync if either file is replaced.
 *
 * VImg has no aspect-ratio of its own, so Vuetify derives one from whichever
 * image first reports natural dimensions and VResponsive turns it into a
 * padding-bottom on its sizer. In the browser that is the bundled placeholder,
 * measurable before the hydration render; on the server nothing is measurable,
 * so the sizer comes out as style="" and every server-rendered page logs a
 * hydration style mismatch. Passing the ratio explicitly makes both sides emit
 * the same sizer — and it is the value the client already computed on its own,
 * so nothing about the rendered box changes (VIP-12333).
 */
const placeholderAspectRatios = {
  bundle: 200 / 156,
  bottle: 150 / 250
}

const placeholderVariant = computed(() =>
  isBundle || useHeroImage ? 'bundle' : 'bottle'
)

const placeholderImage = computed(
  () => placeholderImages[placeholderVariant.value]
)

const aspectRatio = computed(
  () => placeholderAspectRatios[placeholderVariant.value]
)

const onResize = () => {
  updateImageDimensions()
}

// Measure the VImg box, not the wrapper: the wrapper fills the parent column
// and carries the bundle pt-2 padding, so it overshoots the rendered image.
// The first real measurement replaces the prop-based estimate (CSS can
// constrain the box below the props); afterwards grow-only, since a larger
// cached image scales down fine while shrinking would build a new URL
// (= new download) for an image we already display.
const updateImageDimensions = useDebounceFn(() => {
  const el = vImgRef.value?.$el
  if (!el) return

  // Product images are square-ish, so if one dimension measures 0 (e.g. not
  // yet laid out), the other is a better estimate than keeping it at 0.
  const measuredHeight = el.clientHeight || el.clientWidth
  const measuredWidth = el.clientWidth || el.clientHeight
  if (!measuredHeight && !measuredWidth) return // hidden: keep the estimate

  if (hasMeasured) {
    imgHeight.value = Math.max(Number(imgHeight.value) || 0, measuredHeight)
    imgWidth.value = Math.max(Number(imgWidth.value) || 0, measuredWidth)
  } else {
    imgHeight.value = measuredHeight || imgHeight.value
    imgWidth.value = measuredWidth || imgWidth.value
    hasMeasured = true
  }
}, 300)

// Function to set fetchpriority on img elements
const setFetchPriority = () => {
  if (!imgRef.value) return
  const imgElements = imgRef.value.querySelectorAll('img')
  imgElements.forEach((img) => {
    img.setAttribute('fetchpriority', 'high')
  })
}

let observer = null

onMounted(() => {
  density.value = window.devicePixelRatio ?? 1
})

onMounted(() => {
  if (!imgRef.value || !observeFetchPriority) return

  setFetchPriority()

  observer = new MutationObserver(() => {
    setFetchPriority()
  })

  observer.observe(imgRef.value, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src']
  })

  watch(image, async () => {
    await nextTick()
    setFetchPriority()
  })
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
})
</script>

<template>
  <div
    ref="imgRef"
    class="product-image position-relative d-flex flex-column align-center justify-center"
    :class="{ 'pt-2': isBundle }"
  >
    <VImg
      ref="vImgRef"
      v-resize="onResize"
      :lazy-src="placeholderImage"
      :src="image"
      :aspect-ratio="aspectRatio"
      :alt="alt"
      :height="height"
      :width="width"
      :class="{ 'high-contrast': highContrastEnabled }"
      :itemprop="addItemProp ? 'image' : null"
      :itemtype="addItemProp ? 'https://schema.org/image' : null"
      :content="addItemProp ? image : ''"
      :options="{ rootMargin: '100%' }"
      :eager="eager"
      fetchpriority="high"
    />
  </div>
</template>

<style scoped>
.product-image {
  max-width: 100%;
}

.high-contrast {
  filter: contrast(1.5);
}
</style>
